import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Image from '../AppImage';

const NotificationListPopup = ({ onClose, isOpen, onUnreadCountChange }) => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all'); // all, unread, read

  useEffect(() => {
    // Mock notifications data
    const mockNotifications = [
      {
        id: 1,
        type: 'client_interest',
        title: 'عميل جديد مهتم بعقار',
        message: 'أحمد محمد مهتم بشقة في عمان - الدوار الرابع',
        time: 'منذ 5 دقائق',
        read: false,
        icon: 'UserPlus',
        color: '#10B981'
      },
      {
        id: 2,
        type: 'contract',
        title: 'عقد جديد يحتاج موافقة',
        message: 'عقد إيجار رقم #2024-156 في انتظار المراجعة',
        time: 'منذ 15 دقيقة',
        read: false,
        icon: 'FileText',
        color: '#F59E0B'
      },
      {
        id: 3,
        type: 'appointment',
        title: 'موعد قادم',
        message: 'موعد مع العميل سارة أحمد الساعة 10:00 صباحاً',
        time: 'منذ 30 دقيقة',
        read: false,
        icon: 'Calendar',
        color: '#8B5CF6'
      },
      {
        id: 4,
        type: 'property',
        title: 'عقار جديد تمت إضافته',
        message: 'تم إضافة فيلا في الدوار السابع بنجاح',
        time: 'منذ ساعة',
        read: true,
        icon: 'Home',
        color: '#3B82F6'
      },
      {
        id: 5,
        type: 'task',
        title: 'مهمة متأخرة',
        message: 'متابعة مع العميل محمد علي - متأخرة بيومين',
        time: 'منذ ساعتين',
        read: true,
        icon: 'AlertCircle',
        color: '#EF4444'
      },
      {
        id: 6,
        type: 'payment',
        title: 'دفعة جديدة',
        message: 'تم استلام دفعة بقيمة 5000 دينار من العميل خالد',
        time: 'منذ 3 ساعات',
        read: true,
        icon: 'Banknote',
        color: '#10B981'
      },
      {
        id: 7,
        type: 'system',
        title: 'تحديث النظام',
        message: 'تم تحديث النظام إلى الإصدار 2.5.0',
        time: 'منذ 5 ساعات',
        read: true,
        icon: 'Settings',
        color: '#6B7280'
      },
      {
        id: 8,
        type: 'message',
        title: 'رسالة جديدة',
        message: 'رسالة من مدير النظام: اجتماع الفريق غداً',
        time: 'منذ 6 ساعات',
        read: true,
        icon: 'MessageSquare',
        color: '#EC4899'
      }
    ];
    setNotifications(mockNotifications);
  }, []);

  // Notify parent of unread count changes
  useEffect(() => {
    const unreadCount = notifications?.filter(n => !n?.read)?.length;
    if (onUnreadCountChange) {
      onUnreadCountChange(unreadCount);
    }
  }, [notifications, onUnreadCountChange]);

  const filteredNotifications = notifications?.filter(n => {
    if (filter === 'unread') return !n?.read;
    if (filter === 'read') return n?.read;
    return true;
  });

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const handleMarkAsRead = (id) => {
    setNotifications(prev => 
      prev?.map(n => n?.id === id ? { ...n, read: true } : n)
    );
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev?.map(n => ({ ...n, read: true })));
  };

  const handleClearAll = () => {
    if (window.confirm('هل تريد حذف جميع الإشعارات؟')) {
      setNotifications([]);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />
      {/* Popup */}
      <div className="fixed top-20 left-4 md:left-20 bg-background border border-border rounded-xl shadow-2xl z-50 w-96 max-h-[600px] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-primary/20 to-accent/20 p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Icon name="Bell" size={20} color="#DAA520" />
              <h3 className="text-lg font-bold text-foreground">الإشعارات</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-error text-white text-xs font-bold rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-muted/50 rounded-lg transition-colors"
            >
              <Icon name="X" size={18} color="#888" />
            </button>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                filter === 'all' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              الكل ({notifications?.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                filter === 'unread' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              غير مقروءة ({unreadCount})
            </button>
            <button
              onClick={() => setFilter('read')}
              className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                filter === 'read' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              مقروءة ({notifications?.length - unreadCount})
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {filteredNotifications?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
              <Icon name="Bell" size={40} color="#888" />
              <p className="mt-2 text-sm">لا توجد إشعارات</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredNotifications?.map((notification) => (
                <div
                  key={notification?.id}
                  className={`p-4 hover:bg-muted/50 transition-colors cursor-pointer ${
                    !notification?.read ? 'bg-primary/5' : ''
                  }`}
                  onClick={() => handleMarkAsRead(notification?.id)}
                >
                  <div className="flex gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${notification?.color}20` }}
                    >
                      <Icon name={notification?.icon} size={20} color={notification?.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-semibold text-foreground">
                          {notification?.title}
                        </h4>
                        {!notification?.read && (
                          <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {notification?.message}
                      </p>
                      <span className="text-xs text-muted-foreground/70 mt-1 block">
                        {notification?.time}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {notifications?.length > 0 && (
          <div className="p-3 border-t border-border bg-muted/30 flex gap-2">
            <Button
              onClick={handleMarkAllAsRead}
              variant="ghost"
              className="flex-1 text-xs h-8"
              disabled={unreadCount === 0}
            >
              تعليم الكل كمقروء
            </Button>
            <Button
              onClick={handleClearAll}
              variant="ghost"
              className="flex-1 text-xs h-8 text-error hover:bg-error/10"
            >
              حذف الكل
            </Button>
          </div>
        )}

        {/* Powered By Footer */}
        <div className="p-3 border-t border-border bg-muted/30 flex items-center justify-center gap-2">
          <span className="text-xs text-muted-foreground">Powered by</span>
          <Image
            src="/assets/images/k-icon-tiny-1768207136633.png"
            alt="Powered by logo"
            className="h-4 w-auto object-contain"
          />
        </div>
      </div>
    </>
  );
};

export default NotificationListPopup;